import { useState, type ReactNode } from "react";

import { useInviteUsers } from "../../api/queries";
import type { InviteEntry, UserRole } from "../../api/types";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { inputClassName } from "../../ui/Field";
import { MAX_INVITES, isValidEmail, roleOptions } from "./people";

interface InviteRow {
  key: number;
  email: string;
  name: string;
  role: UserRole;
}

interface InviteOutcome {
  sent: string[];
  existing: string[];
}

function blankRow(key: number): InviteRow {
  return { key, email: "", name: "", role: "member" };
}

/**
 * `users.invite` takes up to twenty `{email, name, role}` entries in one call
 * (`docs/spec/users-and-teams.md` §6). An address that already has an account is
 * not an error — it comes back in `data.users` and is simply not in `data.sent`,
 * which is how this dialog tells the two apart and says so.
 */
export function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  const [rows, setRows] = useState<InviteRow[]>([blankRow(0)]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [outcome, setOutcome] = useState<InviteOutcome | null>(null);
  const invite = useInviteUsers();

  const reset = (): void => {
    setRows([blankRow(0)]);
    setErrors({});
    setOutcome(null);
    invite.reset();
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const update = (key: number, patch: Partial<InviteRow>): void => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const submit = (): void => {
    const filled = rows.filter((row) => row.email.trim() !== "" || row.name.trim() !== "");
    const nextErrors: Record<number, string> = {};
    for (const row of filled) {
      if (!isValidEmail(row.email)) nextErrors[row.key] = "Enter a valid email address";
    }
    if (filled.length === 0) {
      nextErrors[rows[0]?.key ?? 0] = "Enter at least one email address";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const invites: InviteEntry[] = filled.map((row) => ({
      email: row.email.trim(),
      name: row.name.trim(),
      role: row.role,
    }));

    invite.mutate(invites, {
      onSuccess: (result) => {
        const sent = result.sent.map((entry) => entry.email);
        setOutcome({
          sent,
          existing: invites.map((entry) => entry.email).filter((email) => !sent.includes(email)),
        });
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Invite to workspace"
      description="They receive an email with a link. An invitation works even for an address outside the allowed domains."
      width="lg"
    >
      {outcome === null ? (
        <>
          <ul className="flex flex-col gap-3">
            {rows.map((row, index) => (
              <li key={row.key} className="flex items-start gap-2">
                <div className="flex-1">
                  <label className="sr-only" htmlFor={`invite-email-${row.key}`}>
                    {`Email address ${index + 1}`}
                  </label>
                  <input
                    id={`invite-email-${row.key}`}
                    type="email"
                    placeholder="person@example.com"
                    value={row.email}
                    onChange={(event) => {
                      update(row.key, { email: event.target.value });
                    }}
                    className={inputClassName}
                  />
                  {errors[row.key] !== undefined && (
                    <p role="alert" className="mt-1 text-xs text-danger">
                      {errors[row.key]}
                    </p>
                  )}
                </div>
                <div className="w-40">
                  <label className="sr-only" htmlFor={`invite-name-${row.key}`}>
                    {`Name ${index + 1}`}
                  </label>
                  <input
                    id={`invite-name-${row.key}`}
                    placeholder="Name"
                    value={row.name}
                    onChange={(event) => {
                      update(row.key, { name: event.target.value });
                    }}
                    className={inputClassName}
                  />
                </div>
                <div className="w-32">
                  <label className="sr-only" htmlFor={`invite-role-${row.key}`}>
                    {`Role ${index + 1}`}
                  </label>
                  <select
                    id={`invite-role-${row.key}`}
                    value={row.role}
                    onChange={(event) => {
                      update(row.key, { role: event.target.value as UserRole });
                    }}
                    className={inputClassName}
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {rows.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove invitation ${index + 1}`}
                    onClick={() => {
                      setRows((current) => current.filter((entry) => entry.key !== row.key));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-3 flex items-center gap-3">
            <Button
              size="sm"
              disabled={rows.length >= MAX_INVITES}
              onClick={() => {
                setRows((current) => [
                  ...current,
                  blankRow(Math.max(...current.map((row) => row.key)) + 1),
                ]);
              }}
            >
              Add another
            </Button>
            <span className="text-xs text-muted">{`${rows.length} of ${MAX_INVITES}`}</span>
          </div>

          {invite.isError && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {invite.error.message}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" disabled={invite.isPending} onClick={submit}>
              {invite.isPending ? "Sending…" : "Send invitations"}
            </Button>
          </div>
        </>
      ) : (
        <div role="status">
          <p className="text-sm font-medium">
            {outcome.sent.length === 0
              ? "No invitations were sent."
              : `${outcome.sent.length} invitation${outcome.sent.length === 1 ? "" : "s"} sent.`}
          </p>
          {outcome.sent.length > 0 && (
            <ul aria-label="Invitations sent" className="mt-2 text-sm text-muted">
              {outcome.sent.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          )}
          {outcome.existing.length > 0 && (
            <>
              <p className="mt-4 text-sm font-medium">Already in this workspace</p>
              <ul aria-label="Already a member" className="mt-2 text-sm text-muted">
                {outcome.existing.map((email) => (
                  <li key={email}>{email}</li>
                ))}
              </ul>
            </>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={reset}>Invite more</Button>
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
